/**
 * Focus management utilities for accessibility.
 * Helps maintain focus when DOM elements are added/removed dynamically.
 */

/**
 * Default selector for focusable elements.
 */
export const DEFAULT_FOCUSABLE_SELECTOR =
  'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable]';

/**
 * Checks if an element is focusable.
 */
export function isElementFocusable(el: Element | null): el is HTMLElement {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();

  // Hidden or disabled elements shouldn't be focusable
  const htmlEl = el as HTMLElement;
  if (htmlEl.hasAttribute('disabled')) return false;
  if ((htmlEl as any).hidden) return false;

  // Natural focusable tags or explicit tabindex
  if (
    (tag === 'a' && (el as HTMLAnchorElement).hasAttribute('href')) ||
    ['input', 'select', 'textarea', 'button', 'iframe'].includes(tag)
  ) {
    return true;
  }

  const tabindex = htmlEl.getAttribute('tabindex');
  if (tabindex !== null) {
    const ti = parseInt(tabindex, 10);
    return !isNaN(ti) && ti >= 0;
  }

  if (htmlEl.hasAttribute('contenteditable')) {
    return true;
  }

  return false;
}

/**
 * Finds the nearest focusable element within a container or its siblings.
 * Search strategy:
 * 1) Descendants of start element
 * 2) Siblings after start (and their descendants)
 * 3) Siblings before start (and their descendants)
 * 4) Move up to parent and repeat
 */
export function findNearestFocusable(
  start: HTMLElement,
  selector: string = DEFAULT_FOCUSABLE_SELECTOR
): HTMLElement | null {
  // 0) Check if the start element itself is focusable
  if (isElementFocusable(start) && start.matches(selector)) return start;

  // 1) descendants
  const desc = start.querySelector<HTMLElement>(selector);
  if (isElementFocusable(desc)) return desc;

  // climb up and search siblings around the node
  let node: HTMLElement | null = start;
  while (node && node.parentElement) {
    const parent = node.parentElement;
    const children = Array.from(parent.children) as HTMLElement[];
    const idx = children.indexOf(node);

    // search forwards (after the node)
    for (let i = idx + 1; i < children.length; i++) {
      const child = children[i];
      if (isElementFocusable(child) && child.matches(selector)) return child;
      const found = child.querySelector<HTMLElement>(selector);
      if (isElementFocusable(found)) return found;
    }

    // search backwards (before the node)
    for (let i = idx - 1; i >= 0; i--) {
      const child = children[i];
      if (isElementFocusable(child) && child.matches(selector)) return child;
      const found = child.querySelector<HTMLElement>(selector);
      if (isElementFocusable(found)) return found;
    }

    // as a last attempt at this ancestor level, see if the ancestor itself matches
    if (isElementFocusable(parent) && parent.matches(selector)) return parent;

    // move up
    node = parent;
  }

  // nothing found
  return null;
}

/**
 * Options for focusNearestElement function.
 */
export interface FocusNearestOptions {
  /** CSS selector for focusable elements. Defaults to DEFAULT_FOCUSABLE_SELECTOR. */
  selector?: string;
  /** Delay in milliseconds before attempting focus. Defaults to 0. */
  delay?: number;
  /** Whether to prevent scrolling when focusing. Defaults to true. */
  preventScroll?: boolean;
}

/**
 * Programmatically finds and focuses the nearest focusable element.
 * Useful when you need to manage focus after dynamic DOM changes.
 *
 * @param elementOrSelector - HTMLElement or CSS selector string to start search from
 * @param options - Configuration options
 * @returns Promise that resolves to the focused element, or null if none found
 *
 * @example
 * // Focus nearest element in results panel after it opens
 * await focusNearestElement('.search-results-panel', { delay: 100 });
 *
 * @example
 * // Focus nearest element with custom selector
 * await focusNearestElement(myElement, {
 *   selector: 'button, a[href]',
 *   preventScroll: false
 * });
 */
export function focusNearestElement(
  elementOrSelector: HTMLElement | string,
  options: FocusNearestOptions = {}
): Promise<HTMLElement | null> {
  const { selector = DEFAULT_FOCUSABLE_SELECTOR, delay = 0, preventScroll = true } = options;

  return new Promise((resolve) => {
    setTimeout(() => {
      try {
        let element: HTMLElement | null = null;

        if (typeof elementOrSelector === 'string') {
          element = document.querySelector<HTMLElement>(elementOrSelector);
        } else {
          element = elementOrSelector;
        }

        if (!element) {
          resolve(null);
          return;
        }

        const target = findNearestFocusable(element, selector);
        if (target) {
          try {
            target.focus({ preventScroll });
          } catch {
            // fallback for older browsers
            target.focus();
          }
          resolve(target);
        } else {
          resolve(null);
        }
      } catch (error) {
        // swallow: focus attempts shouldn't crash the app
        resolve(null);
      }
    }, delay);
  });
}
